// src/components/admin/PersonnelInvitation.jsx
import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import Quill from 'quill';
import 'quill/dist/quill.snow.css';
import {
  Send, Mail, User, FileText, X, Paperclip, AlertCircle, CheckCircle,
  Copy, Link as LinkIcon, RefreshCw, Search, ChevronDown, Home, Upload as UploadIcon, Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from '../../utils/axios';
import { formatAuthors } from '../../utils/authors';
import Unauthorized from './UnauthorizedAccess';
import ALLOWED_ROLES from './utils/AllowedRoles';
import { generateRandomToken } from '../../utils/utils.global';
import { showToast } from '../../utils/toast';

const MAX_ATTACHMENTS = 10;
const MAX_FILE_SIZE = 20 * 1024 * 1024; // 20MB

const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  try {
    return new Date(dateString.replace(' ', 'T')).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
    });
  } catch {
    return dateString;
  }
};

const ReviewerGuideYoutubeLink = `https://youtu.be/ivl-_VzTWQw`;
const EditorsGuideYoutubeLnk = `https://youtu.be/ivl-_VzTWQw`;

const PersonnelInvitation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const quillRef = useRef(null);
  const quillInstance = useRef(null);
  const fileInputRef = useRef(null);

  const [responseDeadline, setResponseDeadline] = useState(
    `Two weeks from today (${new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })})`
  );
  const { user } = useAuth();

  const queryParams = new URLSearchParams(location.search);
  const isReviewer = queryParams.get('reviewer') === 'true';
  const isEditor = queryParams.get('editor') === 'true';
  const manuscriptId = queryParams.get('a');

  const isAuthorized = user && ALLOWED_ROLES.includes(user.editorial_level || user.editorialLevel);

  const [manuscriptData, setManuscriptData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const [emailSuggestions, setEmailSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filteredSuggestions, setFilteredSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  const [editorSuggestions, setEditorSuggestions] = useState([]);
  const [showEditorSuggestions, setShowEditorSuggestions] = useState(false);
  const [filteredEditorSuggestions, setFilteredEditorSuggestions] = useState([]);
  const [editorSuggestionsLoading, setEditorSuggestionsLoading] = useState(false);

  const [formData, setFormData] = useState({
    articleId: manuscriptId,
    editor: user?.email || '',
    reviewerEmail: '',
    ccEmail: '',
    bccEmail: '',
    subject: isReviewer
      ? `Invitation to Review Manuscript ${manuscriptId}`
      : `Invitation to Edit Manuscript ${manuscriptId}`,
  });

  const [isHandlingEditor, setIsHandlingEditor] = useState(true);
  const [handlingEditorEmail, setHandlingEditorEmail] = useState('');

  const [ccTags, setCcTags] = useState([]);
  const [bccTags, setBccTags] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [bccInput, setBccInput] = useState('');

  // Attachments: array of { url, name, size, uploading, tempId }
  const [attachments, setAttachments] = useState([]);

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [sending, setSending] = useState(false);
  const [copySuccess, setCopySuccess] = useState('');
  const [invitationLinks, setInvitationLinks] = useState({ accept: '', decline: '', token: '' });
  const [showLinkInfo, setShowLinkInfo] = useState(false);

  // ── Fetch manuscript data ───────────────────────────────────────────────
  useEffect(() => {
    const fetchManuscriptData = async () => {
      if (!manuscriptId) {
        setFetchError('No manuscript ID provided');
        setLoading(false);
        return;
      }
      try {
        setLoading(true);
        const response = await axios.post('/editors/getSubmissionData', { id: manuscriptId });
        if (response.data.error) {
          setFetchError(response.data.error);
          setManuscriptData(null);
        } else if (response.data.success && response.data.articles) {
          const article = response.data.articles;
          let authors = [];
          try {
            authors = typeof article.authors === 'string'
              ? JSON.parse(article.authors)
              : article.authors || [];
          } catch {
            authors = [article.corresponding_author_fullname];
          }
          setManuscriptData({
            id: article.revision_id,
            title: article.title || 'Untitled',
            correspondingAuthor: article.corresponding_author_fullname || article.corresponding_author || '',
            correspondingEmail: article.corresponding_authors_email || article.corresponding_email || '',
            submittedDate: formatDate(article.date_submitted) || article.last_updated || '',
            abstract: article.unstructured_abstract || '',
            type: article.article_type || 'Research Article',
            authors,
          });
          setFetchError(null);
        }
      } catch (error) {
        console.error('Error fetching manuscript:', error);
        setFetchError(error.response?.data?.error || 'Failed to fetch manuscript data');
        setManuscriptData(null);
      } finally {
        setLoading(false);
      }
    };
    fetchManuscriptData();
  }, [manuscriptId]);

  // ── Fetch email suggestions ─────────────────────────────────────────────
  useEffect(() => {
    const fetchSuggestions = async () => {
      if (!manuscriptId) return;
      setSuggestionsLoading(true);
      try {
        const endpoint = isReviewer ? '/editors/listOfReviewerEmails' : '/editors/listOfEditorEmails';
        const response = await axios.post(endpoint, { articleId: manuscriptId });
        if (response.data.success && response.data.emails) setEmailSuggestions(response.data.emails);
      } catch (error) {
        console.error('Error fetching email suggestions:', error);
      } finally {
        setSuggestionsLoading(false);
      }
    };
    fetchSuggestions();
  }, [isReviewer, manuscriptId]);

  useEffect(() => {
    const fetchEditorSuggestions = async () => {
      if (!manuscriptId || !isReviewer) return;
      setEditorSuggestionsLoading(true);
      try {
        const response = await axios.post('/editors/listOfEditorEmails', { articleId: manuscriptId });
        if (response.data.success && response.data.emails) setEditorSuggestions(response.data.emails);
      } catch (error) {
        console.error('Error fetching editor email suggestions:', error);
      } finally {
        setEditorSuggestionsLoading(false);
      }
    };
    fetchEditorSuggestions();
  }, [isReviewer, manuscriptId]);

  useEffect(() => {
    if (handlingEditorEmail && handlingEditorEmail.length > 0) {
      const filtered = editorSuggestions.filter(email =>
        email.toLowerCase().includes(handlingEditorEmail.toLowerCase()));
      setFilteredEditorSuggestions(filtered);
      setShowEditorSuggestions(filtered.length > 0);
    } else {
      setFilteredEditorSuggestions([]);
      setShowEditorSuggestions(false);
    }
  }, [handlingEditorEmail, editorSuggestions]);

  useEffect(() => {
    if (formData.reviewerEmail && formData.reviewerEmail.length > 0) {
      const filtered = emailSuggestions.filter(email =>
        email.toLowerCase().includes(formData.reviewerEmail.toLowerCase()));
      setFilteredSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      setFilteredSuggestions([]);
      setShowSuggestions(false);
    }
  }, [formData.reviewerEmail, emailSuggestions]);

  // ── Quill init ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (!quillRef.current || !manuscriptData || quillInstance.current) return;
    quillInstance.current = new Quill(quillRef.current, {
      modules: {
        toolbar: [
          ['bold', 'italic'],
          ['link', 'blockquote', 'code-block'],
          [{ list: 'ordered' }, { list: 'bullet' }],
          [{ header: [1, 2, false] }],
          [{ color: [] }],
          ['clean'],
        ],
      },
      theme: 'snow',
      placeholder: 'Compose your invitation message...',
    });
    updateTemplate();
  }, [manuscriptData]);

  useEffect(() => {
    if (quillInstance.current && manuscriptData && formData.reviewerEmail) updateTemplate();
  }, [formData.reviewerEmail, manuscriptData, isReviewer, invitationLinks]);

  // ── Invitation link generation ──────────────────────────────────────────
  useEffect(() => {
    if (formData.reviewerEmail && manuscriptId) {
      const baseUrl = window.location.origin;
      const token = generateRandomToken(30);
      const role = isReviewer ? 'reviewer' : 'editor';
      const email = encodeURIComponent(formData.reviewerEmail);
      const redirectPath = isReviewer ? `reviewerdash/review/${manuscriptId}` : null;
      const params = new URLSearchParams({
        type: role,
        id: manuscriptId,
        email,
        token,
        ...(redirectPath ? { url: redirectPath } : {}),
      });
      const acceptLink = `${baseUrl}/invitation/accept?${params.toString()}`;
      const declineLink = `${baseUrl}/invitation/decline?type=${role}&id=${manuscriptId}&email=${email}&token=${token}`;
      setInvitationLinks({ accept: acceptLink, decline: declineLink, token });
    }
  }, [formData.reviewerEmail, manuscriptId, isReviewer]);

  const updateTemplate = () => {
    if (!quillInstance.current || !manuscriptData) return;
    quillInstance.current.setContents(getFilledTemplate());
  };

  const getFilledTemplate = () => {
    const today = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const twoWeeksLater = new Date();
    twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
    const ThreeDaysLater = new Date();
    ThreeDaysLater.setDate(ThreeDaysLater.getDate() + 3);
    const deadlineDate = twoWeeksLater.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    const ThreeDaysLaterDeadline = ThreeDaysLater.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    setResponseDeadline(
      `${!isReviewer ? `Two Weeks from today (${deadlineDate})` : `Three Days from today (${ThreeDaysLaterDeadline})`}`
    );

    const invitedName = formData.reviewerEmail
      ? formData.reviewerEmail.split('@')[0].replace(/[.-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
      : `[Name of Invited ${isReviewer ? 'Reviewer' : 'Editor'}]`;

    return [
      { insert: `"${manuscriptData.title}" (${manuscriptId})\n\n` },
      { insert: `${today}\n\n` },
      { insert: invitedName + '\n\n' },
      { insert: `The above-titled manuscript has been submitted to the ASFI Research Journal (ASFIRJ). I would be delighted if you consider ${isReviewer ? 'reviewing' : 'editing'} the manuscript for possible publication in ASFIRJ as it is a subject in which I know that you have great interest and expertise. The abstract is given at the end of this email for your reference.\n\n` },
      { insert: `I would appreciate if you can respond to this invitation on whether or not you are able to ${isReviewer ? 'review' : 'edit'} the manuscript on or before ${isReviewer ? ThreeDaysLaterDeadline : deadlineDate}.\n\n` },
      { insert: 'ASFIRJ is the official journal of the African Science Frontiers Initiatives (ASFI). It is a peer-reviewed international, open access, and multidisciplinary journal, publishing original papers, expert reviews, systematic reviews and meta-analyses, position papers, guidelines, protocols, data, editorials, news and commentaries, research letters from any research field.\n\n' },
      { attributes: { bold: true }, insert: 'ASFIRJ Policy on the Use of AI in Peer Review\n\n' },
      { insert: 'ASFIRJ prohibits the use of generative AI or AI-assisted technologies in the peer review process, including the preparation of manuscript review reports. Reviewers must not upload submitted manuscripts, or any portion thereof, into generative AI tools (e.g., ChatGPT, Grammarly, or similar applications) during the review process. Such use may compromise the confidentiality of unpublished manuscripts and expose authors\' intellectual property to third-party AI systems. As peer review is a confidential, independent, and human intellectual endeavor, reviewers are expected to conduct their scientific assessment and prepare their review reports without the assistance of generative AI or AI-assisted technologies.\n\n' },
      { insert: `If you accept the invitation, the manuscript files will be available for you on your ASFIRJ ${isReviewer ? 'Reviewer' : 'Editor'} Dashboard.\n\n` },
      { insert: `Upon your acceptance, you will have 14 days to turn in your ${isReviewer ? 'review' : 'editorial decision'}.\n\n` },
      { insert: `ASFIRJ has detailed instructions for ${isReviewer ? 'reviewers' : 'editors'} on how to perform their duties and how to organize their report here: ` },
      { attributes: { link: `https://asfirj.org/${isReviewer ? 'reviewers' : 'editors'}.html` }, insert: `https://asfirj.org/${isReviewer ? 'reviewers' : 'editors'}.html` },
      { insert: '\n\n' },
      { insert: `Also checkout this video tutorial on how to ${isReviewer ? 'access and submit reviews on ASFIRJ' : 'perform editorial duties on ASFIRJ'}. ` },
      { attributes: { link: `${isReviewer ? ReviewerGuideYoutubeLink : EditorsGuideYoutubeLnk}` }, insert: `${isReviewer ? ReviewerGuideYoutubeLink : EditorsGuideYoutubeLnk}` },
      { insert: '\n\n' },
      { attributes: { color: '#e60000', bold: true }, insert: 'Accept Invitation: ' },
      { attributes: { link: invitationLinks.accept, color: '#0066cc' }, insert: invitationLinks.accept || '[Link to Accept]' },
      { insert: '\n\n' },
      { attributes: { color: '#e60000', bold: true }, insert: 'Decline Invitation: ' },
      { attributes: { link: invitationLinks.decline, color: '#0066cc' }, insert: invitationLinks.decline || '[Link to Decline]' },
      { insert: '\n\nSincerely,\n' },
      { attributes: { bold: true }, insert: user?.fullname || 'Editor' },
      { insert: '\nEditor\n\nsubmissions@asfirj.org\nASFI Research Journal\nExcellence. Quality. Impact\n"Raising the bar of scientific publishing in Africa"\n' },
      { attributes: { link: 'https://asfirj.org/' }, insert: 'https://asfirj.org/' },
      { insert: '\n' },
      { attributes: { link: 'mailto:asfirj@asfirj.org' }, insert: 'asfirj@asfirj.org' },
      { insert: '\n\n' },
      { attributes: { bold: true }, insert: 'MANUSCRIPT DETAILS' },
      { insert: '\n\n' },
      { attributes: { bold: true }, insert: 'TITLE' },
      { insert: `: "${manuscriptData.title}"\n\n` },
      { attributes: { bold: true }, insert: 'AUTHORS' },
      { insert: `: ${formatAuthors(manuscriptData.authors)}\n\n` },
      { attributes: { bold: true }, insert: 'TYPE' },
      { insert: `: ${manuscriptData.type}\n\n` },
      { attributes: { bold: true }, insert: 'SUBMITTED DATE' },
      { insert: `: ${manuscriptData.submittedDate}\n\n` },
      { attributes: { bold: true }, insert: 'ABSTRACT:' },
      { insert: '\n' },
      { attributes: { bold: true }, insert: 'Background: ' },
      { insert: `${manuscriptData.abstract || 'Abstract not available'}\n` },
      { insert: '\n' },
    ];
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSuggestionClick = (email) => {
    setFormData(prev => ({ ...prev, reviewerEmail: email }));
    setShowSuggestions(false);
  };

  const handleEditorSuggestionClick = (email) => {
    setHandlingEditorEmail(email);
    setShowEditorSuggestions(false);
  };

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addCcEmail(); }
    else if (e.key === 'Backspace' && ccInput === '' && ccTags.length > 0) removeCcTag(ccTags.length - 1);
  };
  const handleCcBlur = () => { if (ccInput.trim()) addCcEmail(); };
  const addCcEmail = () => {
    const email = ccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !ccTags.includes(email)) {
      const next = [...ccTags, email];
      setCcTags(next);
      setFormData(prev => ({ ...prev, ccEmail: next.join(', ') }));
      setCcInput('');
    }
  };
  const handleBccKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',' || e.key === ' ') { e.preventDefault(); addBccEmail(); }
    else if (e.key === 'Backspace' && bccInput === '' && bccTags.length > 0) removeBccTag(bccTags.length - 1);
  };
  const handleBccBlur = () => { if (bccInput.trim()) addBccEmail(); };
  const addBccEmail = () => {
    const email = bccInput.trim().replace(/,/g, '');
    if (email && isValidEmail(email) && !bccTags.includes(email)) {
      const next = [...bccTags, email];
      setBccTags(next);
      setFormData(prev => ({ ...prev, bccEmail: next.join(', ') }));
      setBccInput('');
    }
  };
  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  const removeCcTag = (i) => {
    const next = ccTags.filter((_, idx) => idx !== i);
    setCcTags(next);
    setFormData(prev => ({ ...prev, ccEmail: next.join(', ') }));
  };
  const removeBccTag = (i) => {
    const next = bccTags.filter((_, idx) => idx !== i);
    setBccTags(next);
    setFormData(prev => ({ ...prev, bccEmail: next.join(', ') }));
  };

  // ── Attachments ────────────────────────────────────────────────────────
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []);
    if (e.target) e.target.value = '';
    if (!files.length) return;

    if (attachments.length + files.length > MAX_ATTACHMENTS) {
      showToast(`Maximum ${MAX_ATTACHMENTS} files allowed`, 'error');
      return;
    }

    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        showToast(`${file.name} exceeds the 20MB limit`, 'error');
        continue;
      }
      await uploadAttachment(file);
    }
  };

  const uploadAttachment = async (file) => {
    const tempId = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

    setAttachments(prev => [
      ...prev,
      { tempId, name: file.name, size: file.size, url: '', uploading: true },
    ]);

    try {
      const fd = new FormData();
      fd.append('manuscriptId', manuscriptId || 'general');
      fd.append('attachment', file);

      const res = await axios.post('/editors/upload-invitation-attachment', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const url = res.data?.fileUrl || res.data?.url;
      if (!url) throw new Error(res.data?.message || 'Upload failed — no URL returned');

      setAttachments(prev =>
        prev.map(a => (a.tempId === tempId ? { ...a, url, uploading: false } : a))
      );
      showToast(`${file.name} uploaded`, 'success');
    } catch (err) {
      console.error('Attachment upload failed:', err);
      showToast(
        err.response?.data?.message || err.message || `Failed to upload ${file.name}`,
        'error'
      );
      setAttachments(prev => prev.filter(a => a.tempId !== tempId));
    }
  };

  const removeAttachment = (tempId) => {
    setAttachments(prev => prev.filter(a => a.tempId !== tempId));
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const anyUploading = attachments.some(a => a.uploading);

  const handleCopyLink = (link, type) => {
    navigator.clipboard.writeText(link);
    showToast(`${type} link copied to clipboard`, 'success');
  };

  const handleCopy = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopySuccess(label);
    setTimeout(() => setCopySuccess(''), 2000);
  };

  const handleResetTemplate = () => {
    if (quillInstance.current && manuscriptData) {
      updateTemplate();
      showToast('Template reset with latest data', 'success');
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (anyUploading) {
      showToast('Please wait for attachments to finish uploading', 'error');
      return;
    }
    if (isReviewer && !isHandlingEditor) {
      if (!handlingEditorEmail.trim()) {
        showToast("Please enter the handling editor's email", 'error');
        return;
      }
      if (!isValidEmail(handlingEditorEmail.trim())) {
        showToast("Please enter a valid handling editor's email", 'error');
        return;
      }
    }
    setShowConfirmModal(true);
  };

  const confirmSend = async () => {
    setSending(true);
    setShowConfirmModal(false);

    try {
      const editorContent = quillInstance.current.getContents();

      const payload = {
        articleId: formData.articleId,
        reviewerEmail: formData.reviewerEmail,
        ccEmail: formData.ccEmail,
        bccEmail: formData.bccEmail,
        subject: formData.subject,
        message: JSON.stringify(editorContent.ops || editorContent),
        invitationType: isReviewer ? 'reviewer' : 'editor',
        acceptLink: invitationLinks.accept,
        declineLink: invitationLinks.decline,
        token: invitationLinks.token,
        attachments: attachments
          .filter(a => a.url)
          .map(a => ({ url: a.url, name: a.name, size: a.size })),
      };

      if (isReviewer && !isHandlingEditor) {
        payload.handlingEditorEmail = handlingEditorEmail.trim();
      }

      const endpoint = isReviewer
        ? '/editors/email/InviteReviewer'
        : '/editors/email/inviteEditor';

      const response = await axios.post(endpoint, payload, {
        headers: { 'Content-Type': 'application/json' },
        timeout: 60000,
      });

      if (response.data.status === 'success') {
        showToast(
          response.data.message || `Invitation sent successfully to ${formData.reviewerEmail}`,
          'success'
        );
        setTimeout(() => navigate('/editors/dashboard'), 1500);
      } else if (response.data.status === 'warning') {
        showToast(response.data.message || 'Invitation already sent', 'warning');
        setSending(false);
      } else {
        showToast(response.data.message || 'Failed to send invitation', 'error');
        setSending(false);
      }
    } catch (error) {
      console.error('Error sending invitation:', error);
      showToast(
        error.response?.data?.message || error.message || 'Failed to send invitation. Please try again.',
        'error'
      );
      setSending(false);
    }
  };

  // ── Loading / error UI ─────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading manuscript data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (fetchError || !manuscriptData) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="max-w-2xl mx-auto">
          <div className="bg-white rounded-lg shadow-md p-8 text-center">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-10 h-10 text-red-600" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-2">Manuscript Not Found</h3>
            <p className="text-gray-600 mb-6">{fetchError || 'No manuscript data found for the provided ID'}</p>
            <button
              onClick={() => navigate('/editors/dashboard')}
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Home className="w-5 h-5 mr-2" />
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!isReviewer && !isEditor) {
    return (
      <div className="container-fluid px-4 py-6">
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6 text-center">
          <AlertCircle className="w-12 h-12 text-yellow-500 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Invalid Invitation Type</h3>
          <p className="text-gray-600">Please specify either reviewer or editor in the URL parameters.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <h3 className="text-2xl font-bold text-gray-800 flex items-center">
            <Mail className="w-6 h-6 mr-2 text-purple-600" />
            Invite {isReviewer ? 'Reviewer' : 'Editor'}
          </h3>
          <button
            type="button"
            onClick={handleResetTemplate}
            className="px-3 py-1 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            Reset Template
          </button>
        </div>
        <p className="text-gray-600 mt-1">
          Send an invitation to {isReviewer ? 'review' : 'edit'} manuscript {manuscriptId}
        </p>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6">
        <form onSubmit={handleSubmit} id="sendMail">
          <input type="hidden" name="articleId" value={manuscriptId} readOnly />

          {/* Handling Editor Section */}
          {isReviewer && (
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Handling Editor</h4>
              <div className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Are you the handling editor?</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {isHandlingEditor
                        ? `Correspondence will be sent to your email (${user?.email || 'your account'})`
                        : "Enter the handling editor's email below"}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={isHandlingEditor}
                    onClick={() => setIsHandlingEditor(!isHandlingEditor)}
                    className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors flex-shrink-0 ${
                      isHandlingEditor ? 'bg-purple-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block w-4 h-4 transform bg-white rounded-full transition-transform ${
                      isHandlingEditor ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>

                {!isHandlingEditor && (
                  <div className="mt-4 relative">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Handling Editor's Email <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                        value={handlingEditorEmail}
                        onChange={(e) => setHandlingEditorEmail(e.target.value)}
                        onFocus={() => filteredEditorSuggestions.length > 0 && setShowEditorSuggestions(true)}
                        required
                        autoComplete="off"
                      />
                      {editorSuggestionsLoading && (
                        <div className="absolute right-3 top-2.5">
                          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
                        </div>
                      )}
                      {!editorSuggestionsLoading && editorSuggestions.length > 0 && (
                        <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                      )}
                    </div>
                    {showEditorSuggestions && filteredEditorSuggestions.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {filteredEditorSuggestions.map((email, index) => (
                          <button
                            key={index}
                            type="button"
                            className="w-full text-left px-4 py-2 hover:bg-purple-50 border-b border-gray-100 last:border-0"
                            onClick={() => handleEditorSuggestionClick(email)}
                          >
                            <span className="text-sm text-gray-700">{email}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Recipient Section */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Recipient</h4>
            <div className="space-y-4">
              <div className="relative">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {isReviewer ? 'Reviewer' : 'Editor'} Email <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent pr-10"
                    name="reviewerEmail"
                    value={formData.reviewerEmail}
                    onChange={handleInputChange}
                    onFocus={() => filteredSuggestions.length > 0 && setShowSuggestions(true)}
                    required
                    autoComplete="off"
                  />
                  {suggestionsLoading && (
                    <div className="absolute right-3 top-2.5">
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-purple-600" />
                    </div>
                  )}
                  {!suggestionsLoading && emailSuggestions.length > 0 && (
                    <Search className="absolute right-3 top-2.5 w-5 h-5 text-gray-400" />
                  )}
                </div>
                {showSuggestions && filteredSuggestions.length > 0 && (
                  <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                    {filteredSuggestions.map((email, index) => (
                      <button
                        key={index}
                        type="button"
                        className="w-full text-left px-4 py-2 hover:bg-purple-50 border-b border-gray-100 last:border-0"
                        onClick={() => handleSuggestionClick(email)}
                      >
                        <span className="text-sm text-gray-700">{email}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">CC</label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-500 min-h-[42px]">
                  {ccTags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-blue-100 text-blue-700">
                      {tag}
                      <button type="button" onClick={() => removeCcTag(index)} className="ml-1 hover:text-blue-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={ccInput}
                    onChange={(e) => setCcInput(e.target.value)}
                    onKeyDown={handleCcKeyDown}
                    onBlur={handleCcBlur}
                    placeholder={ccTags.length === 0 ? 'Enter CC emails (comma separated)' : ''}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">BCC</label>
                <div className="flex flex-wrap items-center gap-2 p-2 border border-gray-300 rounded-lg focus-within:ring-2 focus-within:ring-purple-500 min-h-[42px]">
                  {bccTags.map((tag, index) => (
                    <span key={index} className="inline-flex items-center px-2 py-1 rounded-md text-sm bg-gray-100 text-gray-700">
                      {tag}
                      <button type="button" onClick={() => removeBccTag(index)} className="ml-1 hover:text-gray-900">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                  <input
                    type="text"
                    className="flex-1 min-w-[200px] outline-none border-0 p-1 text-sm"
                    value={bccInput}
                    onChange={(e) => setBccInput(e.target.value)}
                    onKeyDown={handleBccKeyDown}
                    onBlur={handleBccBlur}
                    placeholder={bccTags.length === 0 ? 'Enter BCC emails (comma separated)' : ''}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Subject */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">Subject</h4>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
            />
          </div>

          {/* Message Body */}
          <div className="mb-6">
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-lg font-semibold text-gray-800">Message Body</h4>
              <div className="text-sm text-gray-600 bg-blue-50 px-3 py-1 rounded-full">Auto-filled with manuscript data</div>
            </div>

            <div className="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-sm text-yellow-800"><strong>Deadline for response:</strong> {responseDeadline}</p>
            </div>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div ref={quillRef} style={{ height: '400px' }} />
            </div>
          </div>

          {/* Attachments */}
          <div className="mb-6">
            <h4 className="text-lg font-semibold text-gray-800 mb-4">
              Attachments (Max: {MAX_ATTACHMENTS} Files)
            </h4>

            <input
              ref={fileInputRef}
              type="file"
              className="hidden"
              multiple
              onChange={handleFileChange}
              accept="*/*"
              disabled={attachments.length >= MAX_ATTACHMENTS}
            />

            <div
              onClick={() => attachments.length < MAX_ATTACHMENTS && fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer ${
                attachments.length >= MAX_ATTACHMENTS
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : 'border-gray-300 hover:border-purple-500'
              }`}
            >
              <UploadIcon className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600">Click to select files</p>
              <p className="text-xs text-gray-500 mt-1">Up to {MAX_ATTACHMENTS} files, max 20MB each</p>
            </div>

            {attachments.length > 0 && (
              <div className="mt-4 space-y-2">
                {attachments.map((file) => (
                  <div
                    key={file.tempId}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center flex-1 min-w-0">
                      {file.uploading ? (
                        <Loader className="w-4 h-4 text-purple-600 mr-2 animate-spin flex-shrink-0" />
                      ) : (
                        <Paperclip className="w-4 h-4 text-gray-400 mr-2 flex-shrink-0" />
                      )}
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-700 truncate">{file.name}</p>
                        <p className="text-xs text-gray-500">
                          {file.uploading ? 'Uploading…' : formatFileSize(file.size)}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeAttachment(file.tempId)}
                      className="p-1 text-gray-400 hover:text-red-500 hover:bg-white rounded flex-shrink-0"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-center mt-8">
            <button
              type="submit"
              disabled={!formData.reviewerEmail || !formData.subject || sending || anyUploading}
              className="px-8 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            >
              {sending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Sending...
                </>
              ) : anyUploading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Uploading attachments...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Invitation
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center mb-4">
                <div className="flex-shrink-0 w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <Mail className="w-6 h-6 text-purple-600" />
                </div>
                <div className="ml-4">
                  <h3 className="text-lg font-semibold text-gray-900">Confirm Send</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Are you sure you want to send this invitation to {formData.reviewerEmail}?
                  </p>
                </div>
              </div>
              <div className="mt-6 flex justify-end gap-3">
                <button
                  onClick={() => setShowConfirmModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmSend}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Send className="w-4 h-4" />
                  Send Invitation
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <footer className="footer text-center py-4 border-t border-gray-200 mt-8">
        <p className="text-sm text-gray-500">
          {new Date().getFullYear()} © ASFI - all rights reserved
        </p>
      </footer>
    </div>
  );
};

export default PersonnelInvitation;